import { getToken } from './authApiClient';

const API_BASE_URL = (import.meta.env.VITE_API_URL || '') + '/api';

export type ProgressEvent = {
    type: 'progress' | 'connected';
    task?: string;
    message?: string;
};

export class NotificationService {
    private eventSource: EventSource | null = null;
    private listeners: ((event: ProgressEvent) => void)[] = [];

    connect() {
        if (this.eventSource) return;

        const token = getToken();
        if (!token) return;

        // We need to pass the token. EventSource doesn't support headers natively in all browsers,
        // but we can pass it as a query param if the backend supports it, or use a polyfill.
        // However, our backend middleware checks headers['authorization'].
        // Standard EventSource API limitations make this tricky.
        // A common workaround is to use a library like `event-source-polyfill` which supports headers.
        // Or, we can modify the backend to accept token in query string for this specific endpoint.
        // Let's assume we can modify the backend to accept query param for SSE.

        // For now, let's try to use the native EventSource and assume we might need to adjust the backend auth middleware
        // to check query params for the /notifications/stream endpoint.

        // Actually, let's modify the backend auth middleware to check query param 'token' as well.
        // But first, let's write this client.

        // Assuming backend accepts token in query param:
        this.eventSource = new EventSource(`${API_BASE_URL}/notifications/stream?token=${token}`);

        this.eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                this.notifyListeners(data);
            } catch (e) {
                console.error('Failed to parse SSE message', e);
            }
        };

        this.eventSource.onerror = (error) => {
            console.error('SSE Error', error);
            this.disconnect();
            // Retry logic could go here
        };
    }

    disconnect() {
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }
    }

    subscribe(listener: (event: ProgressEvent) => void) {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }

    private notifyListeners(event: ProgressEvent) {
        this.listeners.forEach(listener => listener(event));
    }
}

export const notificationService = new NotificationService();
